import type { LucideIcon } from "lucide-react";
import { solutions } from "./solutions";
import { industryTopics } from "./industryTopics";

export type HubTopicCard = {
  label: string;
  href: string;
  summary?: string;
  icon?: LucideIcon;
  accent?: "teal" | "emerald";
};

export type HubTopicCardGroup = {
  heading: string;
  cards: HubTopicCard[];
  /** When true, the group renders with reduced visual weight (for minority add-ons). */
  dimmed?: boolean;
};

export type NavMegaMenuItem = {
  label: string;
  href: string;
  description?: string;
};

export type NavMegaMenuColumn = {
  heading?: string;
  items: NavMegaMenuItem[];
};

const topicBySlug = new Map(industryTopics.map((t) => [t.slug, t]));

const SOLUTION_NAV_LABELS: Record<string, string> = {
  dispute: "Dispute Resolution",
  forensic: "Forensic Audit",
  rca: "Root Cause Analysis",
  sop: "SOP Adherence",
  regulatory: "Regulatory & Audit Defense",
  incident: "Incident Response & SOC",
  vendor: "Vendor & BPO Assurance",
  workplace: "Workplace Integrity",
  "content-safety": "Content Safety & Policy",
  "process-coaching": "Process Coaching",
  "cctv-surveillance": "CCTV AI Surveillance",
  "physical-security-msp": "MSP Physical Security",
};

const SOLUTION_NAV_DESCRIPTIONS: Record<string, string> = {
  dispute: "Neutralize costly finger-pointing with indisputable visual evidence",
  forensic: "Investigation-ready evidence that cannot be altered",
  rca: "Visual bug reports that eliminate guesswork",
  sop: "See exactly how remote and outsourced teams follow your processes",
  regulatory: "Evidence ready when regulators or auditors ask questions",
  incident: "Give your security team video context for every alert",
  vendor: "Hold contractors to the same standard as your own team",
  workplace: "Facts-based investigations with recording limited to relevant work",
  "content-safety": "Automated explicit content and visual policy alerts on every segment",
  "process-coaching": "Share recordings of top performers as structured coaching material",
  "cctv-surveillance": "Real-time camera alerts and searchable footage archives",
  "physical-security-msp": "Manage camera fleets across multiple client sites",
};

function solutionNavItem(id: string): NavMegaMenuItem {
  return {
    label: SOLUTION_NAV_LABELS[id] ?? id,
    href: `/solutions/${id}`,
    description: SOLUTION_NAV_DESCRIPTIONS[id],
  };
}

/** Four-column Solutions mega menu; includes every entry in `solutions`. */
export const solutionsMegaMenuColumns: NavMegaMenuColumn[] = [
  {
    heading: "Evidence & Accountability",
    items: ["dispute", "forensic", "regulatory", "workplace"].map(solutionNavItem),
  },
  {
    heading: "Operations & Quality",
    items: ["rca", "sop", "vendor", "process-coaching"].map(solutionNavItem),
  },
  {
    heading: "Security & Compliance",
    items: ["incident", "content-safety"].map(solutionNavItem),
  },
  {
    heading: "CCTV+ Add-on (sold separately)",
    items: ["cctv-surveillance", "physical-security-msp"].map(solutionNavItem),
  },
];

const INDUSTRY_NAV_GROUPS: { heading: string; slugs: string[] }[] = [
  { heading: "Financial Services", slugs: ["banking", "insurance"] },
  { heading: "Life Sciences", slugs: ["pharma", "medical-devices"] },
  { heading: "Professional Services", slugs: ["legal", "adops", "bpo-call-centers"] },
  { heading: "Operations", slugs: ["logistics", "architecture", "realestate"] },
  {
    heading: "Technology & Public",
    slugs: ["saas", "telecom", "ecommerce", "government", "security-operations"],
  },
];

/** Industries mega menu; includes every entry in `industryTopics`. */
export const industriesMegaMenuColumns: NavMegaMenuColumn[] = INDUSTRY_NAV_GROUPS.map((group) => ({
  heading: group.heading,
  items: group.slugs.map((slug) => {
    const topic = topicBySlug.get(slug);
    return {
      label: topic?.label ?? slug,
      href: `/industries/${slug}`,
    };
  }),
}));

/** Platform dropdown (also used in footer). */
export const platformNavItems: NavMegaMenuItem[] = [
  {
    label: "Security Architecture",
    href: "/platform/security",
    description: "CISO-grade security design and compliance",
  },
  {
    label: "Privacy-by-Design",
    href: "/platform/privacy",
    description: "Forget-me model, sovereignty, and audit logs",
  },
  {
    label: "Platform Components",
    href: "/platform/architecture",
    description: "Agents, identity, live streaming, and topology",
  },
  {
    label: "Enterprise Identity",
    href: "/platform/identity",
    description: "Bring-your-own IdP (OIDC), SSO, and RBAC for investigators",
  },
  {
    label: "Deployment Topology",
    href: "/platform/deployment",
    description: "Self-hosted on your VPC and agent rollout",
  },
  {
    label: "AI Intelligence",
    href: "/platform/ai-intelligence",
    description: "Visual search, chat Q&A, and enterprise AI compliance features",
  },
  {
    label: "Session Audio Capture",
    href: "/features#session-audio",
    description: "Optional add-on: system and mic audio muxed into desktop session segments",
  },
  {
    label: "Webcam Capture",
    href: "/features#webcam-capture",
    description: "Optional add-on: workplace identity snapshots paired with screen evidence",
  },
  {
    label: "CCTV AI Pipeline",
    href: "/platform/cctv-ai-pipeline",
    description: "Optional CCTV+ add-on: hot path alerts and cold path indexing for NVR streams",
  },
];

/** Resources dropdown (also used in footer). */
export const resourcesNavItems: NavMegaMenuItem[] = [
  {
    label: "White Papers & Resources",
    href: "/resources",
    description: "Guides, white papers, and packaged articles",
  },
  {
    label: "FAQs",
    href: "/faq",
    description: "Answers by category: plans, roles, deployment, compliance",
  },
  {
    label: "Session Audio Guide",
    href: "/resources/guide-session-audio-capture",
    description: "System and mic audio streaming, plan limits, and consent workflows",
  },
  {
    label: "Forensic replay & export",
    href: "/resources/guide-session-audio-forensic-replay",
    description: "Stereo split replay, desk forensic ZIP, partial export behavior",
  },
  {
    label: "Webcam Capture guide",
    href: "/resources/guide-webcam-capture",
    description: "Identity snapshots, cadence matrix, indicator disclosure",
  },
  {
    label: "CCTV+ Plugin",
    href: "/pricing#cctv-plugin",
    description: "Optional camera add-on, purchased separately on Pro or Enterprise",
  },
  {
    label: "Webcam Capture pricing",
    href: "/pricing#webcam-capture-addon",
    description: "Optional identity verification module (Pro and Enterprise tiers)",
  },
  {
    label: "Session Audio pricing",
    href: "/pricing#session-audio-addon",
    description: "Optional session audio modules purchased per seat",
  },
];

export function flattenMegaMenuColumns(columns: NavMegaMenuColumn[]): NavMegaMenuItem[] {
  return columns.flatMap((col) => col.items);
}

export function megaMenuToFooterLinks(columns: NavMegaMenuColumn[]): { label: string; href: string }[] {
  return flattenMegaMenuColumns(columns).map((item) => ({
    label: item.label,
    href: item.href,
  }));
}

export function navItemsToFooterLinks(items: NavMegaMenuItem[]): { label: string; href: string }[] {
  return items.map((item) => ({ label: item.label, href: item.href }));
}

const solutionById = new Map(solutions.map((s) => [s.id, s]));

const DIMMED_SOLUTION_GROUPS = new Set(["CCTV+ Add-on (sold separately)"]);

/** Grouped cards for the /solutions hub page. */
export function buildSolutionHubGroups(): HubTopicCardGroup[] {
  return solutionsMegaMenuColumns.map((col) => ({
    heading: col.heading ?? "Solutions",
    dimmed: DIMMED_SOLUTION_GROUPS.has(col.heading ?? ""),
    cards: col.items.map((item) => {
      const id = item.href.replace("/solutions/", "");
      const sol = solutionById.get(id);
      return {
        label: item.label,
        href: item.href,
        summary: item.description,
        icon: sol?.icon,
        accent: sol?.accent,
      } satisfies HubTopicCard;
    }),
  }));
}

/** Flat card list for the /industries hub page. */
export function buildIndustryHubCards(): HubTopicCard[] {
  return industryTopics.map((topic) => ({
    label: topic.label,
    href: `/industries/${topic.slug}`,
    summary: topic.keyRisks.slice(0, 2).join(" · "),
    icon: topic.icon,
    accent: topic.accentColor,
  }));
}

/** Dev-time guard: nav must list every solution page. */
if (import.meta.env.DEV) {
  const navIds = new Set(
    solutionsMegaMenuColumns.flatMap((col) => col.items.map((item) => item.href.replace("/solutions/", ""))),
  );
  for (const sol of solutions) {
    if (!navIds.has(sol.id)) {
      console.warn(`[navMenus] Solution "${sol.id}" is missing from solutionsMegaMenuColumns`);
    }
  }

  const industrySlugs = new Set(
    industriesMegaMenuColumns.flatMap((col) => col.items.map((item) => item.href.replace("/industries/", ""))),
  );
  for (const topic of industryTopics) {
    if (!industrySlugs.has(topic.slug)) {
      console.warn(`[navMenus] Industry "${topic.slug}" is missing from industriesMegaMenuColumns`);
    }
  }
}
